export const translations = {
  en: {
    nav: {
      about: "About",
      services: "Services",
      how: "How",
      calculator: "Calculator",
      faq: "FAQ",
      contact: "Contact",
      cta: "Book a Consultation",
    },
    hero: {
      eyebrow: "Intelligent Automation. Built for Business.",
      headline_1: "Automate Your Business.",
      headline_2: "Eliminate Manual Work.",
      subheadline:
        "UiPath, Power Automate & chatbot consulting and training for modern businesses. 5+ years of enterprise-grade RPA experience delivered from Ploiești & Bucharest to the world.",
      cta_primary: "Book a Consultation",
      cta_secondary: "Contact Me",
      stat_a: "Years automating",
      stat_b: "Hours automated",
      stat_c: "People trained",
      stat_d: "Industries served",
    },
    about: {
      eyebrow: "About",
      heading: "I build software helpers that do your team's boring work for them.",
      badge: "5+ years automating",
      person_name: "Stoicescu Florin",
      role: "Enterprise RPA & Power Platform Consultant",
      body_1:
        "I'm a freelance automation specialist. If your team spends hours every week copying data between systems, processing invoices, sending repetitive emails or filling out the same spreadsheets — I build small digital workers (people call them \"bots\") that do that work for them, automatically, day and night, without mistakes.",
      body_2:
        "I've been doing this for over 5 years, mostly with UiPath, Microsoft Power Automate and AI chatbots. My job is to find the parts of your workday that don't actually need a human, hand them over to a bot, and train your team to keep things running smoothly afterwards.",
      highlights: [
        { label: "Find", text: "I look at how your team works and spot the tasks that can be automated." },
        { label: "Build", text: "I build the bots — reliable, easy to update, no surprises." },
        { label: "Hand over", text: "I document everything and train your team to own it after I leave." },
      ],
    },
    why_automate: {
      eyebrow: "Why Automate",
      heading: "Five reasons businesses choose automation.",
      items: [
        { title: "Save hundreds of work hours each month", body: "Manual tasks that take your team a full day get done in minutes — and they never stop." },
        { title: "Reduce human errors", body: "Bots don't get tired, distracted or forget steps. The same input produces the same correct output, every time." },
        { title: "Faster processing times", body: "Invoices, reports, approvals and onboarding flows that took days now finish in hours — or seconds." },
        { title: "Available 24/7", body: "Your bots keep working overnight, on weekends and during holidays. No sick days, no lunch breaks." },
        { title: "Scales without hiring", body: "When volume doubles, you don't need to double headcount. You just let the bot run longer." },
      ],
    },
    services: {
      eyebrow: "Services",
      heading: "What I build for you",
      items: [
        {
          tag: "01 / RPA",
          title: "RPA Development",
          subtitle: "UiPath",
          bullets: [
            "End-to-end process automation",
            "Legacy & web system integration",
            "Bot development, monitoring, maintenance",
          ],
        },
        {
          tag: "02 / Power Platform",
          title: "Power Automate Solutions",
          subtitle: "Cloud + Desktop",
          bullets: [
            "Cloud flows & approvals",
            "Power Automate Desktop (PAD)",
            "Email, Excel & SharePoint automation",
          ],
        },
        {
          tag: "03 / AI Chatbots",
          title: "Chatbots & AI Assistants",
          subtitle: "Customer & Internal",
          bullets: [
            "Customer support chatbots",
            "Internal HR / IT helpdesk bots",
            "WhatsApp, web and Teams integrations",
          ],
        },
        {
          tag: "04 / Enablement",
          title: "Training & Workshops",
          subtitle: "Corporate",
          bullets: [
            "Corporate training sessions",
            "Beginner to advanced automation",
            "Hands-on practical exercises",
          ],
        },
      ],
      discuss_cta: "Discuss this service",
    },
    how: {
      eyebrow: "How it Works",
      heading: "From first call to live automation, in five steps.",
      steps: [
        { title: "Free consultation", body: "We discuss your current process — no jargon, no obligation." },
        { title: "Process analysis", body: "I identify automation opportunities and estimate the savings." },
        { title: "Development", body: "The automation is built and tested in a safe environment." },
        { title: "Deployment", body: "The solution is implemented inside your business environment." },
        { title: "Support", body: "Ongoing maintenance, monitoring and improvements." },
      ],
    },
    examples: {
      eyebrow: "Examples",
      heading: "Processes I can automate for you",
      sub: "If any of these sound like something your team does manually today, it's a strong candidate for automation.",
      items: [
        "Reading invoices from email and entering them into ERP",
        "Moving data between Excel files",
        "Generating daily / weekly reports",
        "Employee onboarding workflows",
        "Approval processes",
        "Customer notifications",
        "CRM updates",
        "Data validation",
      ],
    },
    stack: {
      eyebrow: "Tech Stack",
      heading: "Tools I live in every day",
    },
    calculator: {
      eyebrow: "Savings Calculator",
      heading: "How much could you save by automating one process?",
      sub: "Move the sliders to match a real task your team does today.",
      hours_label: "Hours per week the task takes (per person)",
      employees_label: "Number of employees doing it",
      cost_label: "Average hourly cost (€)",
      monthly_label: "Potential monthly savings",
      annual_label: "Potential annual savings",
      hours_saved_label: "Hours given back to your team / year",
      footnote: "Estimate based on full automation of the task. Real-world results depend on process complexity, exceptions and edge cases — most of my projects pay for themselves in under 6 months.",
      cta: "Get a precise estimate for your process",
    },
    why: {
      eyebrow: "Why work with me",
      heading: "Expert delivery. Business-first thinking.",
      items: [
        { title: "Enterprise RPA experience", body: "Years building, scaling and rescuing automation programs in real enterprise environments." },
        { title: "Practical, real-world focus", body: "Automation that survives change requests, audits and the people who actually use it." },
        { title: "Process optimization mindset", body: "I rework the process before automating it — so you don't pay to automate the wrong thing." },
        { title: "Clean handover", body: "Clear documentation, naming conventions and maintainability built in from day one." },
        { title: "Training for mixed audiences", body: "Comfortable teaching both developers and business users without losing either of them." },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Why use this technology?",
      sub: "The questions clients ask me most often.",
      items: [
        {
          q: "What is RPA, in plain English?",
          a: "RPA (Robotic Process Automation) is software that mimics what a person does on a computer — clicking buttons, reading emails, copying data between apps. The difference: it does it 24/7, without errors, and 5–10× faster.",
        },
        {
          q: "Will this replace my employees?",
          a: "No. It takes over the parts of their job they hate doing — the manual, repetitive copy-paste work. Your people move up to higher-value tasks: handling exceptions, talking to customers, making decisions.",
        },
        {
          q: "How long does a typical project take?",
          a: "A simple automation (one process, one system) usually goes live in 2–4 weeks. Larger end-to-end automations with multiple systems take 1–3 months. The free consultation gives you a realistic estimate for your case.",
        },
        {
          q: "Do I need IT knowledge to work with you?",
          a: "Not at all. Most of my clients are business owners or operations leads, not developers. I translate everything into plain language and only involve your IT team when we actually need them (usually just for access setup).",
        },
        {
          q: "How secure are the automations?",
          a: "The bots run inside your own environment, with the same security rules as your employees. Credentials are stored in encrypted vaults, every action is logged, and access is restricted to what each bot strictly needs. They are typically safer than human users.",
        },
        {
          q: "What happens when my process changes?",
          a: "I write automations with change in mind — clean structure, clear naming and documentation. Most updates take hours, not weeks. Support packages cover ongoing changes so you're never stuck.",
        },
        {
          q: "What does it cost?",
          a: "Each project is quoted based on complexity, not hours. You'll know the full price before any work starts. Most clients see full ROI within 3–6 months of go-live.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      heading: "Let's discuss your automation needs.",
      body:
        "Tell me about the process you want to automate, the team you want to train, or the workflow that keeps breaking. I usually reply within one business day.",
      email_label: "Email",
      whatsapp_label: "WhatsApp",
      phone_label: "Phone",
      linkedin_label: "LinkedIn",
      whatsapp_value: "+40 725 318 017",
      phone_value: "+40 725 318 017",
      location: "Ploiești & Bucharest, Romania",
      form: {
        name: "Your name",
        email: "Your email",
        message: "Tell me about your project",
        submit: "Send message",
        sending: "Sending…",
        success: "Message sent. I'll get back to you shortly.",
        error: "Something went wrong. Please email me directly.",
        invalid: "Please fill in name, a valid email and a message.",
      },
    },
    footer: {
      rights: "All rights reserved.",
      built: "Automating the boring parts of business since day one.",
    },
    cookies: {
      title: "This site uses cookies.",
      body:
        "We use cookies and local storage to make the site work, remember your language choice and understand how visitors use the page. No cookies are sold or shared with advertisers.",
      learn_more: "What we store",
      hide_details: "Hide details",
      accept: "Accept all",
      decline: "Necessary only",
      details: [
        { label: "Necessary", text: "Language preference (EN / RO), cookie consent itself." },
        { label: "Analytics", text: "Anonymous page-view stats so I can see which sections work." },
        { label: "No marketing", text: "No ad tracking, no third-party sharing of your data." },
      ],
    },
  },
  ro: {
    nav: {
      about: "Despre",
      services: "Servicii",
      how: "Cum",
      calculator: "Calculator",
      faq: "FAQ",
      contact: "Contact",
      cta: "Programează o consultație",
    },
    hero: {
      eyebrow: "Automatizare inteligentă. Construită pentru business.",
      headline_1: "Automatizează-ți afacerea.",
      headline_2: "Elimină munca manuală.",
      subheadline:
        "Consultanță și training UiPath, Power Automate și chatboți pentru companii moderne. Peste 5 ani de experiență RPA de nivel enterprise, livrată din Ploiești și București pentru toată lumea.",
      cta_primary: "Programează o consultație",
      cta_secondary: "Contactează-mă",
      stat_a: "Ani de automatizare",
      stat_b: "Ore automatizate",
      stat_c: "Persoane formate",
      stat_d: "Industrii deservite",
    },
    about: {
      eyebrow: "Despre",
      heading: "Construiesc asistenți software care fac munca plictisitoare în locul echipei tale.",
      badge: "5+ ani de automatizare",
      person_name: "Stoicescu Florin",
      role: "Consultant RPA & Power Platform la nivel enterprise",
      body_1:
        "Sunt consultant freelance de automatizare. Dacă echipa ta petrece ore în șir copiind date între sisteme, procesând facturi, trimițând email-uri repetitive sau completând aceleași tabele Excel — construiesc mici „lucrători digitali\" (oamenii le spun „boți\") care fac toată munca aceea în locul lor, automat, zi și noapte, fără greșeli.",
      body_2:
        "Fac asta de peste 5 ani, în principal cu UiPath, Microsoft Power Automate și chatboți cu AI. Treaba mea este să identific părțile din ziua voastră de muncă ce nu au nevoie de un om, să le pasez unui bot și să formez echipa pentru a menține totul funcțional după aceea.",
      highlights: [
        { label: "Identific", text: "Mă uit la cum lucrează echipa ta și descopăr ce poate fi automatizat." },
        { label: "Construiesc", text: "Construiesc boții — siguri, ușor de actualizat, fără surprize." },
        { label: "Predau", text: "Documentez totul și formez echipa ta să preia după plecarea mea." },
      ],
    },
    why_automate: {
      eyebrow: "De ce să automatizezi",
      heading: "Cinci motive pentru care companiile aleg automatizarea.",
      items: [
        { title: "Economisește sute de ore de muncă în fiecare lună", body: "Sarcini manuale care îți țin echipa ocupată o zi întreagă se fac în minute — și nu se opresc niciodată." },
        { title: "Reduce erorile umane", body: "Boții nu obosesc, nu se distrag și nu uită pași. Aceeași intrare produce același rezultat corect, de fiecare dată." },
        { title: "Timp de procesare mai rapid", body: "Facturi, rapoarte, aprobări și onboarding care durau zile se termină acum în ore — sau secunde." },
        { title: "Disponibil 24/7", body: "Boții tăi lucrează noaptea, în weekend și de sărbători. Fără zile libere, fără pauze de masă." },
        { title: "Scalează fără angajări", body: "Când volumul se dublează, nu trebuie să dublezi echipa. Pur și simplu lași botul să ruleze mai mult." },
      ],
    },
    services: {
      eyebrow: "Servicii",
      heading: "Ce construiesc pentru tine",
      items: [
        {
          tag: "01 / RPA",
          title: "Dezvoltare RPA",
          subtitle: "UiPath",
          bullets: [
            "Automatizare end-to-end a proceselor",
            "Integrare cu sisteme legacy și web",
            "Dezvoltare, monitorizare și mentenanță boți",
          ],
        },
        {
          tag: "02 / Power Platform",
          title: "Soluții Power Automate",
          subtitle: "Cloud + Desktop",
          bullets: [
            "Fluxuri în cloud și aprobări",
            "Power Automate Desktop (PAD)",
            "Automatizare email, Excel și SharePoint",
          ],
        },
        {
          tag: "03 / Chatboți AI",
          title: "Chatboți & asistenți AI",
          subtitle: "Clienți & intern",
          bullets: [
            "Chatboți de suport pentru clienți",
            "Asistenți interni HR / IT helpdesk",
            "Integrare WhatsApp, web și Teams",
          ],
        },
        {
          tag: "04 / Enablement",
          title: "Training și workshop-uri",
          subtitle: "Corporate",
          bullets: [
            "Sesiuni de training corporate",
            "De la începător la avansat",
            "Exerciții practice, hands-on",
          ],
        },
      ],
      discuss_cta: "Discută acest serviciu",
    },
    how: {
      eyebrow: "Cum funcționează",
      heading: "De la primul apel la automatizare live, în cinci pași.",
      steps: [
        { title: "Consultație gratuită", body: "Discutăm despre procesul tău actual — fără jargon, fără obligație." },
        { title: "Analiză de proces", body: "Identific oportunitățile de automatizare și estimez economiile." },
        { title: "Dezvoltare", body: "Automatizarea este construită și testată într-un mediu sigur." },
        { title: "Implementare", body: "Soluția este implementată în mediul tău de business." },
        { title: "Suport", body: "Mentenanță continuă, monitorizare și îmbunătățiri." },
      ],
    },
    examples: {
      eyebrow: "Exemple",
      heading: "Procese pe care le pot automatiza pentru tine",
      sub: "Dacă oricare dintre acestea sună ca ceva ce echipa ta face manual astăzi, este un candidat solid pentru automatizare.",
      items: [
        "Citirea facturilor din email și introducerea lor în ERP",
        "Mutarea datelor între fișiere Excel",
        "Generarea de rapoarte zilnice / săptămânale",
        "Fluxuri de onboarding angajați",
        "Procese de aprobare",
        "Notificări către clienți",
        "Actualizări CRM",
        "Validare de date",
      ],
    },
    stack: {
      eyebrow: "Tehnologii",
      heading: "Uneltele cu care lucrez zilnic",
    },
    calculator: {
      eyebrow: "Calculator de economii",
      heading: "Cât ai putea economisi automatizând un singur proces?",
      sub: "Ajustează cursoarele pentru a corespunde unei activități reale pe care echipa ta o face astăzi.",
      hours_label: "Ore pe săptămână (per persoană)",
      employees_label: "Număr de angajați care fac asta",
      cost_label: "Cost mediu pe oră (€)",
      monthly_label: "Economii lunare potențiale",
      annual_label: "Economii anuale potențiale",
      hours_saved_label: "Ore redate echipei tale / an",
      footnote: "Estimare bazată pe automatizarea completă a sarcinii. Rezultatele reale depind de complexitatea procesului, excepții și cazuri limită — majoritatea proiectelor mele se amortizează în mai puțin de 6 luni.",
      cta: "Vreau o estimare precisă pentru procesul meu",
    },
    why: {
      eyebrow: "De ce să lucrezi cu mine",
      heading: "Livrare expert. Gândire orientată spre business.",
      items: [
        { title: "Experiență RPA la nivel enterprise", body: "Ani de construit, scalat și salvat programe de automatizare în medii enterprise reale." },
        { title: "Focus practic, din realitate", body: "Automatizare care rezistă la change request-uri, audit și oamenii care chiar o folosesc." },
        { title: "Mindset de optimizare", body: "Reoptimizez procesul înainte de a-l automatiza — ca să nu plătești ca să automatizezi greșit." },
        { title: "Handover curat", body: "Documentație clară, convenții de naming și mentenabilitate din ziua unu." },
        { title: "Training pentru audiențe mixte", body: "Predau confortabil atât developerilor cât și utilizatorilor de business — fără să-i pierd pe niciunii." },
      ],
    },
    faq: {
      eyebrow: "Întrebări frecvente",
      heading: "De ce să folosești această tehnologie?",
      sub: "Întrebările pe care mi le pun cel mai des clienții.",
      items: [
        {
          q: "Ce este RPA, pe înțelesul tuturor?",
          a: "RPA (Robotic Process Automation) este software care imită ce face un om pe calculator — apăsat butoane, citit email-uri, copiat date între aplicații. Diferența: o face 24/7, fără erori și de 5–10 ori mai rapid.",
        },
        {
          q: "Asta îmi va înlocui angajații?",
          a: "Nu. Preia părțile pe care le urăsc oricum — munca manuală, repetitivă, de copy-paste. Oamenii tăi avansează la sarcini cu valoare mai mare: gestionarea excepțiilor, relația cu clienții, luarea de decizii.",
        },
        {
          q: "Cât durează un proiect tipic?",
          a: "O automatizare simplă (un proces, un sistem) intră în producție de obicei în 2–4 săptămâni. Automatizările mai mari, end-to-end, cu mai multe sisteme, iau 1–3 luni. Consultația gratuită îți oferă o estimare realistă pentru cazul tău.",
        },
        {
          q: "Trebuie să am cunoștințe IT ca să lucrăm împreună?",
          a: "Deloc. Majoritatea clienților mei sunt antreprenori sau lideri de operațiuni, nu developeri. Traduc totul în limbaj simplu și implic echipa ta IT doar când chiar este nevoie (de obicei doar pentru acces).",
        },
        {
          q: "Cât de sigure sunt automatizările?",
          a: "Boții rulează în propriul tău mediu, cu aceleași reguli de securitate ca angajații. Credențialele sunt în seifuri criptate, fiecare acțiune este logată, iar accesul este restricționat strict la ce are nevoie fiecare bot. De regulă, sunt mai siguri decât utilizatorii umani.",
        },
        {
          q: "Ce se întâmplă când procesul meu se schimbă?",
          a: "Scriu automatizările gândind la schimbare — structură curată, naming clar și documentație. Cele mai multe actualizări iau ore, nu săptămâni. Pachetele de suport acoperă modificările continue, ca să nu rămâi blocat.",
        },
        {
          q: "Cât costă?",
          a: "Fiecare proiect este cotat în funcție de complexitate, nu de ore. Vei ști prețul complet înainte să înceapă orice muncă. Majoritatea clienților își recuperează investiția în 3–6 luni de la go-live.",
        },
      ],
    },
    contact: {
      eyebrow: "Contact",
      heading: "Hai să discutăm despre nevoile tale de automatizare.",
      body:
        "Spune-mi despre procesul pe care vrei să-l automatizezi, echipa pe care vrei să o formezi sau workflow-ul care continuă să se strice. De obicei răspund într-o zi lucrătoare.",
      email_label: "Email",
      whatsapp_label: "WhatsApp",
      phone_label: "Telefon",
      linkedin_label: "LinkedIn",
      whatsapp_value: "+40 725 318 017",
      phone_value: "+40 725 318 017",
      location: "Ploiești și București, România",
      form: {
        name: "Numele tău",
        email: "Email-ul tău",
        message: "Spune-mi despre proiectul tău",
        submit: "Trimite mesaj",
        sending: "Se trimite…",
        success: "Mesaj trimis. Revin în curând cu un răspuns.",
        error: "A apărut o eroare. Te rog scrie-mi direct pe email.",
        invalid: "Te rog completează nume, email valid și un mesaj.",
      },
    },
    footer: {
      rights: "Toate drepturile rezervate.",
      built: "Automatizez părțile plictisitoare ale business-ului din ziua unu.",
    },
    cookies: {
      title: "Acest site folosește cookie-uri.",
      body:
        "Folosim cookie-uri și stocare locală pentru ca site-ul să funcționeze, să rețină limba aleasă și să înțelegem cum este utilizat. Cookie-urile nu sunt vândute sau partajate cu agenți de publicitate.",
      learn_more: "Ce stocăm",
      hide_details: "Ascunde detalii",
      accept: "Accept tot",
      decline: "Doar necesare",
      details: [
        { label: "Necesare", text: "Preferința de limbă (EN / RO) și consimțământul pentru cookie-uri." },
        { label: "Analytics", text: "Statistici anonime de vizualizări, ca să văd ce secțiuni funcționează." },
        { label: "Fără marketing", text: "Fără tracking publicitar, fără partajarea datelor cu terți." },
      ],
    },
  },
};
